/**
 * **Personal data on screen** (W23, FR-51, FR-52, ADR-0017, plan D69): the
 * library's *Personal data* cell, the document's *Personal data* panel, and the
 * reveal both it and an extraction's masked fields use.
 *
 * - **The cell** says the most sensitive kind found in the current version
 *   (Aadhaar › PAN › card › bank account › mobile › e-mail) with *also contains
 *   …* behind an ⓘ — or *None found*, *Not scanned* (and why), *Checking*. It is
 *   the service's `personal_data`, never a guess.
 * - **The panel** lists the current version's findings as the service answers
 *   them — the kind, the **masked form**, where it is and who found it — and
 *   offers *Reveal* to a person holding `personal_data:can_view`.
 * - **A reveal** is one value, asked for by one click. The service logs the read
 *   before it answers (FR-52); the package keeps the value **in this
 *   component's state and nowhere else** — not in browser storage, not in a
 *   module, not in the HTTP cache — and forgets it the moment the person moves
 *   on: another tab, another document or version, a page left.
 *
 * The file itself is not redacted (ADR-0017 §4): its preview and its download
 * stay under the document's own permission and read log.
 */
import { Alert, Badge, Button, EmptyState, Skeleton, SkeletonRegion, TONE_TEXT, Tooltip, cn } from '@fiveox/ui'
import { Eye, EyeOff, Info } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { plural } from './bits'
import { errorText, useLoad } from './hooks'
import {
  PERSONAL_DATA_KIND_WORDS,
  PERSONAL_DATA_STATE_WORDS,
  alsoContainsText,
  findingSourceText,
  locationText,
  notScannedShort,
  notScannedText,
  personalDataKindLabel,
  type DocumentPersonalData,
  type PersonalDataFinding,
} from './personal-data'
import { ApiError, PERSONAL_DATA, isRevealMismatch, type ProDocClient, type ProDocDocument } from './prodoc-client'

/* ---------------------------------------------------------------- the cell */

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

/**
 * The register's *Personal data* cell (FR-51): the first kind as a badge and
 * the rest behind an ⓘ, or the state's words. A dash for a document whose
 * answer carried no `personal_data` (a service older than W23).
 */
export function PersonalDataSummary({ data, size = 'sm', className }: { data: DocumentPersonalData | null | undefined; size?: 'sm' | 'md'; className?: string }) {
  if (!data) return <span className={cn('text-muted-foreground', className)}>—</span>
  if (data.state === 'found') {
    const first = data.kinds[0]
    const also = alsoContainsText(data.kinds)
    return (
      <span className={cn('inline-flex items-center gap-1', className)} data-personal-data="found">
        <Badge tone="warning" size={size}>
          {first ? PERSONAL_DATA_KIND_WORDS[first] : PERSONAL_DATA_STATE_WORDS.found}
        </Badge>
        {also && (
          // The rest of what it holds — a phrase, so a tooltip; reached by focus as well as hover. Its click stays here, so the row does not open.
          <Tooltip label={also}>
            <span role="img" tabIndex={0} aria-label={also} className="text-muted-foreground inline-flex cursor-help" onClick={stop} onKeyDown={stop} data-also>
              <Info size={14} aria-hidden />
            </span>
          </Tooltip>
        )}
      </span>
    )
  }
  if (data.state === 'not_scanned') {
    const why = notScannedShort(data.reason)
    return (
      <span className={cn('block', className)} data-personal-data="not_scanned">
        <span className="text-muted-foreground text-sm">{PERSONAL_DATA_STATE_WORDS.not_scanned}</span>
        {why && <span className="text-muted-foreground block text-xs">{why}</span>}
      </span>
    )
  }
  return (
    <span className={cn('text-muted-foreground text-sm', className)} data-personal-data={data.state}>
      {PERSONAL_DATA_STATE_WORDS[data.state]}
    </span>
  )
}

/* -------------------------------------------------------------- the reveal */

/** Why a reveal did not show a value, as a person reads it. */
export function revealRefusalText(e: unknown): string {
  if (isRevealMismatch(e)) return 'The file no longer holds this value at that place, so nothing was revealed.'
  if (e instanceof ApiError && e.code === 'FORBIDDEN') return 'Revealing personal data needs a permission you do not hold in this organisation.'
  if (e instanceof ApiError && e.code === 'NOT_FOUND') return 'This is no longer on file — the document may have a newer version.'
  return errorText(e, 'It could not be revealed.')
}

export interface Reveals {
  /** What was revealed, by key — a finding's id or a field's path. Component state only. */
  values: ReadonlyMap<string, unknown>
  /** The key being revealed now, if any. */
  busy: string | null
  errors: Readonly<Record<string, string>>
  reveal: (key: string, ask: () => Promise<unknown>) => Promise<void>
  hide: (key: string) => void
}

/**
 * Revealed values for one screen, **held in this component's state and nowhere
 * else**. A new `scope` — another document, version or extraction — forgets
 * them all, and an answer that arrives after the scope moved on, or after the
 * component is gone, is dropped unseen.
 */
export function useReveals(scope: string): Reveals {
  const [values, setValues] = useState<Map<string, unknown>>(() => new Map())
  const [busy, setBusy] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const live = useRef(true)
  const generation = useRef(0)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  useEffect(() => {
    generation.current += 1
    setValues(new Map())
    setErrors({})
    setBusy(null)
  }, [scope])

  const reveal = useCallback(async (key: string, ask: () => Promise<unknown>) => {
    const asked = generation.current
    const current = () => live.current && generation.current === asked
    setBusy(key)
    setErrors((all) => {
      const next = { ...all }
      delete next[key]
      return next
    })
    try {
      const value = await ask()
      if (!current()) return
      if (value === null || value === undefined) setErrors((all) => ({ ...all, [key]: 'ProDoc answered with no value to show.' }))
      else setValues((all) => new Map(all).set(key, value))
    } catch (e) {
      if (current()) setErrors((all) => ({ ...all, [key]: revealRefusalText(e) }))
    } finally {
      if (current()) setBusy((b) => (b === key ? null : b))
    }
  }, [])

  const hide = useCallback((key: string) => {
    setValues((all) => {
      if (!all.has(key)) return all
      const next = new Map(all)
      next.delete(key)
      return next
    })
  }, [])

  return { values, busy, errors, reveal, hide }
}

/**
 * Whether this person may reveal (`personal_data:can_view`) — the evaluation's
 * own answer, asked only when there is something masked to reveal, so a screen
 * with nothing masked asks nothing more. Fails closed: no answer, no reveal.
 */
export function useMayReveal(client: ProDocClient, needed: boolean): boolean {
  const access = useLoad(() => (needed ? client.access() : Promise.resolve(null)), [client, needed])
  return Boolean(access.data?.permissions[PERSONAL_DATA]?.can_view)
}

/** *Reveal* and *Hide*, with what they are about in their accessible names. */
export function RevealButton({ label, revealed, busy, onReveal, onHide, compact = false }: { label: string; revealed: boolean; busy: boolean; onReveal: () => void; onHide: () => void; compact?: boolean }) {
  if (revealed) {
    return (
      <Button variant="ghost" size="sm" aria-label={`Hide ${label}`} title="Hide it again" onClick={onHide}>
        <EyeOff size={15} aria-hidden />
        {!compact && 'Hide'}
      </Button>
    )
  }
  return (
    <Button variant={compact ? 'ghost' : 'outline'} size="sm" aria-label={`Reveal ${label}`} title="Reveal — the read is logged" onClick={onReveal} loading={busy} loadingText={compact ? undefined : 'Revealing…'}>
      <Eye size={15} aria-hidden />
      {!compact && 'Reveal'}
    </Button>
  )
}

/* --------------------------------------------------------------- the panel */

export interface PersonalDataPanelProps {
  client: ProDocClient
  doc: Pick<ProDocDocument, 'id' | 'current_version_id' | 'personal_data'>
  /** `personal_data:can_view` from the evaluation — whether *Reveal* is offered at all. */
  canReveal: boolean
  /** How many findings are listed before *Show all*. */
  pageSize?: number
}

/**
 * The document's *Personal data* panel (FR-51, FR-52): the current version's
 * findings, masked, and *Reveal* for whoever may. Mount it only while it is
 * open — its revealed values live and die with it.
 */
export function PersonalDataPanel({ client, doc, canReveal, pageSize = 50 }: PersonalDataPanelProps) {
  const findings = useLoad(() => client.documents.personalData.list(doc.id), [client, doc.id, doc.current_version_id])
  const reveals = useReveals(`${doc.id}:${doc.current_version_id}`)
  const [showAll, setShowAll] = useState(false)

  if (findings.loading && !findings.data) {
    return (
      <SkeletonRegion label="Loading the personal data found" className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </SkeletonRegion>
    )
  }
  if (!findings.data) {
    return (
      <Alert tone="error">
        {findings.error || 'The personal data found could not be loaded.'}{' '}
        <Button variant="link" onClick={() => void findings.reload()}>
          Try again
        </Button>
      </Alert>
    )
  }

  const answer = findings.data
  const items = answer.items
  const state = answer.state ?? doc.personal_data?.state ?? null
  const reason = answer.reason ?? doc.personal_data?.reason ?? null
  // The evaluation's word first; the service's `can_reveal` may only narrow it (fail closed).
  const mayReveal = canReveal && answer.can_reveal !== false
  const shown = showAll ? items : items.slice(0, pageSize)

  return (
    <div className="space-y-4" data-personal-data-panel>
      {state === 'pending' && (
        <Alert tone="info">
          <p>Checking this version for personal data. The scan runs shortly after an upload and reads only its text.</p>
          <Button variant="link" onClick={() => void findings.reload()}>
            Look again
          </Button>
        </Alert>
      )}
      {state === 'not_scanned' && (
        <Alert tone="info">
          Not scanned. {notScannedText(reason)} Reading it — from Extractions — scans the text it reads, OCR included
          {items.length ? '; what that found is listed below.' : '.'}
        </Alert>
      )}
      {state === 'none_found' && items.length === 0 && (
        <EmptyState
          variant="outline"
          title="No personal data found"
          description="This version’s text holds no Aadhaar number, individual’s PAN, card number, bank account, mobile number or e-mail address that the scan could find."
        />
      )}

      {items.length > 0 && (
        <div>
          <p className="text-foreground text-sm font-medium">{plural(items.length, 'value')} found in the current version, shown masked</p>
          <ul className="divide-border divide-y" aria-label="Personal data found">
            {shown.map((f) => (
              <FindingRow
                key={f.id}
                finding={f}
                value={reveals.values.get(f.id)}
                error={reveals.errors[f.id]}
                busy={reveals.busy === f.id}
                mayReveal={mayReveal}
                onReveal={() => void reveals.reveal(f.id, () => client.documents.personalData.reveal(doc.id, f.id))}
                onHide={() => reveals.hide(f.id)}
              />
            ))}
          </ul>
          {items.length > shown.length && (
            <Button variant="link" onClick={() => setShowAll(true)}>
              Show all {items.length}
            </Button>
          )}
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        ProDoc shows what it finds masked. The file itself — its preview and its download — is kept as uploaded, under the document’s permission and read log.{' '}
        {mayReveal
          ? 'Each reveal is recorded in the read log before the value is shown, and the value is shown only until you leave this tab.'
          : 'Revealing a value needs the personal-data permission — an administrator’s or a reviewer’s by default.'}
      </p>
    </div>
  )
}

function FindingRow({
  finding,
  value,
  error,
  busy,
  mayReveal,
  onReveal,
  onHide,
}: {
  finding: PersonalDataFinding
  value: unknown
  error: string | undefined
  busy: boolean
  mayReveal: boolean
  onReveal: () => void
  onHide: () => void
}) {
  const kind = personalDataKindLabel(finding.kind)
  const where = locationText(finding.location)
  const revealed = value !== undefined
  return (
    <li className="flex items-start gap-3 py-2.5" data-finding={finding.id} data-kind={finding.kind}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="warning" size="sm">
            {kind}
          </Badge>
          {revealed ? (
            <span className="text-foreground font-mono text-sm break-all" data-revealed>
              {String(value)}
            </span>
          ) : (
            <span className="text-foreground font-mono text-sm break-all" data-masked>
              {finding.masked}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs">{[where, findingSourceText(finding.source)].filter(Boolean).join(' · ')}</p>
        {error && <p className={cn('mt-0.5 text-xs', TONE_TEXT.error)}>{error}</p>}
      </div>
      {mayReveal && <RevealButton label={`${kind} (${where})`} revealed={revealed} busy={busy} onReveal={onReveal} onHide={onHide} />}
    </li>
  )
}
